"use client";
import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  HStack,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  VStack,
  Button,
  Collapse,
  IconButton,
  TableContainer,
  Flex,
  useColorModeValue,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import * as XLSX from "xlsx";
import { appRequest } from "routes";

const TimeLineReport = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  // ✅ Hooks pre-computed
  const bg = useColorModeValue("white", "gray.800");
  const theadBg = useColorModeValue("gray.100", "gray.700");
  const rowHoverBg = useColorModeValue("gray.50", "gray.700");
  const collapseBg = useColorModeValue("gray.50", "gray.900");

  const fetchData = async (pageNum: number) => {
    setLoading(true);
    try {
      const res : any = await appRequest('vendor','reportApprovalData',{ page: pageNum, limit: 10 })
      console.log('the response are', res)
      if(res?.status === "success"){
       setData(res?.data?.data || []);
       setTotalPages(res?.data?.totalPages || 0);
      }
    } catch (err) {
      console.error("Error fetching approvals:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page);
  }, [page]);

  const handleDownloadExcel = () => {
  const workbook = XLSX.utils.book_new();
  const sheetNames = new Set<string>();
  data.forEach((item, index) => {
    const excelData: any[] = [];

    if (item.approvalsTimeline && item.approvalsTimeline.length > 0) {
      item.approvalsTimeline.forEach((approval: any) => {
        excelData.push({
          Vendor: item.vendorName || "NA",
          VendorCode: item.vendorCode || "NA",
          Workflow: item.workflowName || "NA",
          Elapsed: item.timeDifferenceHuman || "NA",
          CreatedAt: item.createdAt
            ? new Date(item.createdAt).toLocaleString()
            : "NA",
          Level: approval.level || "NA",
          Approver: approval.approverName || "NA",
          Status: approval.status || "NA",
          ApprovalDate: approval.approvalDate
            ? new Date(approval.approvalDate).toLocaleString()
            : "NA",
          ApprovalElapsed: approval.approvalElapsedHuman || "NA",
        });
      });
    } else {
      excelData.push({
        Vendor: item.vendorName || "NA",
        VendorCode: item.vendorCode || "NA",
        Workflow: item.workflowName || "NA",
        Elapsed: item.timeDifferenceHuman || "NA",
        CreatedAt: item.createdAt
          ? new Date(item.createdAt).toLocaleString()
          : "NA",
        Level: "NA",
        Approver: "NA",
        Status: "NA",
        ApprovalDate: "NA",
        ApprovalElapsed: "NA",
      });
    }

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // ✅ Clean vendor name for sheet (Excel sheet names cannot exceed 31 chars)
    let sheetName =
      (item.vendorName || `Vendor_${index + 1}`)
        .replace(/[\\/?*[\]:]/g, "") // Remove invalid chars
        .substring(0, 28); // Leave space for suffix if needed

    // ✅ Ensure uniqueness by appending counter if duplicate
    let uniqueName = sheetName;
    let counter = 1;
    while (sheetNames.has(uniqueName)) {
      uniqueName = `${sheetName}_${counter}`;
      counter++;
    }

    sheetNames.add(uniqueName);

    XLSX.utils.book_append_sheet(workbook, worksheet, uniqueName);
  });

  XLSX.writeFile(workbook, "Approvals_Report.xlsx");
};



  if (loading) {
    return (
      <HStack justify="center" py={10}>
        <Spinner size="lg" />
        <Text>Loading approvals...</Text>
      </HStack>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      {/* Header with Download */}
      <Flex justify="space-between" align="center">
        <Text fontSize="xl" fontWeight="bold">
          Approval Timeline Report
        </Text>
        <Button colorScheme="blue" onClick={handleDownloadExcel}>
          Download Excel
        </Button>
      </Flex>

      <TableContainer bg={bg} p={4} rounded="2xl" shadow="sm">
        <Table variant="striped" colorScheme="gray" size="md">
          <Thead bg={theadBg}>
            <Tr>
              <Th></Th>
              <Th>Vendor</Th>
              <Th>Vendor Code</Th>
              <Th>Workflow</Th>
              <Th>Elapsed</Th>
              <Th>Created At</Th>
            </Tr>
          </Thead>
          <Tbody>
            {data.length > 0 ? (
              data.map((item: any) => (
                <React.Fragment key={item._id}>
                  <Tr
                    _hover={{ bg: rowHoverBg }}
                    transition="background 0.2s"
                  >
                    <Td>
                      <IconButton
                        size="sm"
                        variant="ghost"
                        aria-label="Expand"
                        icon={
                          expanded === item._id ? (
                            <ChevronDownIcon />
                          ) : (
                            <ChevronRightIcon />
                          )
                        }
                        onClick={() =>
                          setExpanded(expanded === item._id ? null : item._id)
                        }
                      />
                    </Td>
                    <Td fontWeight="bold">{item.vendorName || "NA"}</Td>
                    <Td>{item.vendorCode || "NA"}</Td>
                    <Td>{item.workflowName || "NA"}</Td>
                    <Td>
                      <Tag colorScheme="purple" rounded="full" px={3}>
                        {item.timeDifferenceHuman || "NA"}
                      </Tag>
                    </Td>
                    <Td>
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleString()
                        : "NA"}
                    </Td>
                  </Tr>

                  {/* Expanded Approvals */}
                  <Tr>
                    <Td colSpan={6} p={0} border="none">
                      <Collapse in={expanded === item._id} animateOpacity>
                        <Box p={4} bg={collapseBg} rounded="md">
                          <Text mb={3} fontWeight="semibold">
                            Approvals Timeline
                          </Text>
                          <Table size="sm" variant="simple">
                            <Thead>
                              <Tr>
                                <Th>Level</Th>
                                <Th>Approver</Th>
                                <Th>Status</Th>
                                <Th>Date</Th>
                                <Th>Elapsed</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {item.approvalsTimeline &&
                              item.approvalsTimeline.length > 0 ? (
                                item.approvalsTimeline.map(
                                  (approval: any, idx: number) => (
                                    <Tr key={idx}>
                                      <Td>{approval.level || "NA"}</Td>
                                      <Td>{approval.approverName || "NA"}</Td>
                                      <Td>
                                        <Tag
                                          colorScheme={
                                            approval.status === "approved"
                                              ? "green"
                                              : approval.status === "rejected"
                                              ? "red"
                                              : approval.status
                                              ? "yellow"
                                              : "gray"
                                          }
                                          rounded="full"
                                          px={3}
                                        >
                                          {approval.status || "NA"}
                                        </Tag>
                                      </Td>
                                      <Td>
                                        {approval.approvalDate
                                          ? new Date(
                                              approval.approvalDate
                                            ).toLocaleString()
                                          : "NA"}
                                      </Td>
                                      <Td color="blue.600">
                                        {approval.approvalElapsedHuman || "NA"}
                                      </Td>
                                    </Tr>
                                  )
                                )
                              ) : (
                                <Tr>
                                  <Td
                                    colSpan={5}
                                    textAlign="center"
                                    color="gray.500"
                                  >
                                    No approvals available
                                  </Td>
                                </Tr>
                              )}
                            </Tbody>
                          </Table>
                        </Box>
                      </Collapse>
                    </Td>
                  </Tr>
                </React.Fragment>
              ))
            ) : (
              <Tr>
                <Td colSpan={6} textAlign="center" color="gray.500">
                  No data available
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <HStack justify="center" spacing={4} py={4}>
        <Button
          size="sm"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          isDisabled={page === 1}
        >
          Previous
        </Button>
        <Text>
          Page {page} of {totalPages}
        </Text>
        <Button
          size="sm"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          isDisabled={page === totalPages}
        >
          Next
        </Button>
      </HStack>
    </VStack>
  );
};

export default TimeLineReport;
